import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import useTranslation from 'hooks/use-translation';

import { FooterList, FooterDivider, Grid, Link } from 'components/core';
import FacebookIcon from 'assets/marketing/facebook';
import InstagramIcon from 'assets/marketing/instagram';
import TwitterIcon from 'assets/marketing/twitter';

import ROUTES from 'routes';
import { REFER_A_DISPENSARY_LINK, RELEASES_LINK } from 'constants/external-links';

export default function GlobalFooterContent({ showTagLine, ...props }) {
  const { t } = useTranslation();
  const CompanyLinks = [
    {
      label: t('common.aboutUs', 'About'),
      href: ROUTES.ABOUT,
    },
    {
      label: t('common.careers', 'Careers'),
      href: ROUTES.CAREERS,
    },
    {
      label: t('common.contactUs', 'Contact'),
      href: ROUTES.CONTACT,
    },
  ];

  const LegalLinks = [
    {
      label: t('common.privacy', 'Privacy Policy'),
      href: ROUTES.PRIVACY,
    },
    {
      label: t('common.terms', 'Terms of Service'),
      href: ROUTES.TERMS,
    },
    {
      label: t('common.accessibility', 'Accessibility'),
      href: ROUTES.ACCESSIBILITY,
    },
    {
      label: t('common.security', 'Security'),
      href: `https://trust.dutchie.com`,
    },
    {
      label: t('common.dispoTerms', 'Dispensary Terms'),
      href: `https://try.dutchie.com/merchant-agreement`,
    },
  ];

  const ResourceLinks = [
    {
      label: t('common.learn', 'Blog'),
      href: ROUTES.BLOG,
    },
    {
      label: t('common.productUpdates', 'Product Updates'),
      href: RELEASES_LINK,
    },
    {
      label: t('common.ReferADispensary', 'Refer a Dispensary'),
      href: REFER_A_DISPENSARY_LINK,
    },
    {
      label: t('common.shopForCannabis', 'Shop for Cannabis'),
      href: ROUTES.HOME,
    },
    {
      label: t('common.dispensariesByCity', 'Dispensaries by City'),
      href: ROUTES.CITIES,
    },
  ];

  const SolutionsForDispensaryLinks1 = [
    {
      label: t('common.dispensaryHomepage', 'Home'),
      href: ROUTES.DISPENSARY_B2B,
    },
    {
      label: t('common.pos', 'Point of Sale'),
      href: ROUTES.DISPENSARY_POINT_OF_SALE,
    },
    {
      label: t('common.ecommerce', 'Ecommerce'),
      href: ROUTES.DISPENSARY_ECOMMERCE,
    },
    {
      label: t('common.payments', 'Payments'),
      href: ROUTES.DISPENSARY_PAYMENTS,
    },
    {
      label: t('common.integrations', 'Integrations'),
      href: ROUTES.DISPENSARY_INTEGRATIONS,
    },
  ];

  // TODO: add back at second wave of  rebrand
  // const SolutionsForDispensaryLinks2 = [
  //   {
  //     label: t('common.singleDispensary', 'Single Dispensary'),
  //     href: ROUTES.SINGLE_DISPENSARY,
  //   },
  //   {
  //     label: t('common.verticallyIntegrated', 'Vertically Integrated'),
  //     href: ROUTES.VERTICALLY_INTEGRAGED,
  //   },
  //   {
  //     label: t('common.multipleLocations', 'Multiple Locations'),
  //     href: ROUTES.MULTIPLE_LOCATIONS,
  //   },
  //   {
  //     label: t('common.multiStateOperator', 'Multi-State Operator'),
  //     href: ROUTES.MULTIPLE_STATE_OPERATOR,
  //   },
  // ];

  const FooterLinkList = () => (
    <FooterLinks>
      {_.map(LegalLinks, (entry) => (
        <Link href={entry.href} key={entry.label}>
          {/* adding in href as it is a warning in CircleCI */}
          <FooterLink href={entry.href}>{entry.label}</FooterLink>
        </Link>
      ))}
    </FooterLinks>
  );

  return (
    <Container className='footer-container'>
      <ListsHolder>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <FooterListContainer gridArea='company'>
          <FooterList title={t('common.company', 'Company')}>
            {_.map(CompanyLinks, (entry) => (
              <Link key={entry.href} href={entry.href}>
                {/* adding in href as it is a warning in CircleCI */}
                <a href={entry.href}>{entry.label}</a>
              </Link>
            ))}
          </FooterList>
        </FooterListContainer>

        {/* eslint-disable-next-line i18next/no-literal-string */}
        <SolutionsContainer gridArea='solutions'>
          <Heading>{t('footer.solutions', 'Solutions for Dispensaries')}</Heading>
          <Grid container columns={1}>
            <NoHeaderFooterList item xs={8}>
              {_.map(SolutionsForDispensaryLinks1, ({ label, href }) => (
                <Link href={href} key={label}>
                  {label}
                </Link>
              ))}
            </NoHeaderFooterList>

            {/* TODO: add back at second wave of  rebrand */}
            {/* <NoHeaderFooterList item xs={8}>
              {_.map(SolutionsForDispensaryLinks2, ({ key, label }) => (
                <Link href={`${ROUTES.CITY}/${key}`} key={key}>
                  {label}
                </Link>
              ))}
            </NoHeaderFooterList> */}
          </Grid>
        </SolutionsContainer>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <ResourcesContainer gridArea='resources'>
          <FooterList title={t('common.resources', 'Resources')}>
            {_.map(ResourceLinks, (entry) => (
              <Link key={entry.href} href={entry.href}>
                {/* adding in href as it is a warning in CircleCI */}
                <a href={entry.href}>{entry.label}</a>
              </Link>
            ))}
          </FooterList>
        </ResourcesContainer>
      </ListsHolder>
      <FollowUsContainer>
        {showTagLine ? <Tagline>{t('common.tagLine', 'Grow with us.')}</Tagline> : <div />}
        <FlexWrapper>
          <SocialLinks>
            <a
              href='https://twitter.com/getdutchie'
              target='_blank'
              rel='noopener noreferrer'
              aria-label={t('footer.linkToTwitter', "link to dutchie's Twitter")}
            >
              <TwitterIcon />
              <HiddenAccessibilityText>
                {t('footer.linkToTwitter', "link to dutchie's Twitter")}
              </HiddenAccessibilityText>
            </a>
            <a
              href='https://www.facebook.com/getdutchie/'
              target='_blank'
              rel='noopener noreferrer'
              aria-label={t('footer.linkToFacebook', "link to dutchie's Facebook")}
            >
              <FacebookIcon />
              <HiddenAccessibilityText>
                {t('footer.linkToFacebook', "link to dutchie's Facebook")}
              </HiddenAccessibilityText>
            </a>
            <a
              href='https://www.instagram.com/wearedutchie'
              target='_blank'
              rel='noopener noreferrer'
              aria-label={t('footer.linkToInstagram', "link to dutchie's Instagram")}
            >
              <InstagramIcon />
              <HiddenAccessibilityText>
                {t('footer.linkToInstagram', "link to dutchie's Instagram")}
              </HiddenAccessibilityText>
            </a>
          </SocialLinks>
        </FlexWrapper>
      </FollowUsContainer>
      <FooterDivider {...props} />
      <SiteInfo container>
        <Copyright>
          <CopyrightSymbol>Ⓒ</CopyrightSymbol>{' '}
          {`dutchie ${new Date().getFullYear()}. ${t('common.allRightsReserved', 'All Rights Reserved')}`}
        </Copyright>
        <FooterLinkList />
      </SiteInfo>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 50px 84px 31px 82px;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    padding: 40px 31px 32px;
  }
`;

const ListsHolder = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 2fr;
  grid-template-areas: 'company solutions . resources';
  margin-bottom: 100px;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    grid-template-columns: 1fr 1fr 1fr;

    grid-template-areas: 'company solutions resources';
  }

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    grid-template-columns: 1fr 1fr;

    grid-template-areas:
      'company resources'
      'solutions solutions';

    margin-bottom: 86px;
  }
`;

const FlexWrapper = styled.div`
  display: flex;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
    justify-content: center;
  }
`;

const FollowUsContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  align-items: center;
`;

const HiddenAccessibilityText = styled.span`
  display: none;
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;

  > a {
    line-height: 0;
    transition: color linear 0.03s;

    &:hover {
      color: ${({ theme }) => theme.colors.mediumGrey};
    }
  }

  > *:not(:last-child) {
    margin-right: 15px;
  }
`;

const Tagline = styled.h2`
  font-size: 28px;
  font-weight: 400;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    font-size: 50px;
  }
`;

const SiteInfo = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 20px;
  color: #e3e6e7;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    padding-top: 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas:
      'links'
      'copyright';
  }
`;

const FooterLink = styled.a`
  &:not(:first-child),
  &:not(:nth-of-type(4)) {
    margin-left: 40px;
  }

  &:nth-of-type(3):after,
  &:first-child:before {
    content: '';
    margin: 0;
  }

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    margin-bottom: 8px;

    &:not(:first-child),
    &:not(:nth-of-type(4)) {
      margin-left: 0;
    }
  }
`;

const FooterLinks = styled.div`
  display: flex;
  grid-area: links;

  ${({ theme }) => theme.breakpoints.down(`md`)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const Copyright = styled.span`
  grid-area: copyright;
`;
const CopyrightSymbol = styled.span``;

const FooterListContainer = styled.span`
  grid-area: ${({ gridArea }) => gridArea};

  a {
    white-space: nowrap;
  }
`;

const ResourcesContainer = styled(FooterListContainer)`
  justify-self: end;
`;

const SolutionsContainer = styled.div`
  grid-area: ${({ gridArea }) => gridArea};
  padding-top: 2px;
  margin-right: 38px;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin-top: 24px;
  }
`;

const Heading = styled.p`
  font-weight: bold;
  line-height: 19px;
  max-width: 261px;
  white-space: nowrap;
  color: #70a0ca;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    font-size: 13px;
    line-height: 16px;
  }

  &&& {
    font-size: 16px;
  }
`;

const NoHeaderFooterList = styled(FooterList)`
  margin-top: 35px !important;
  color: #e3e6e7;
`;
